import {
  assertEvent,
  assign,
  fromPromise,
  sendTo,
  setup,
  type SnapshotFrom,
} from 'xstate';
import type {IEvent} from '../../types/IEvent';
import {bookingMachine} from '../../machines/bookingMachine';
import {identificationMachine} from '../../machines/identificationMachine';
import {rsvpMachine} from '../../machines/rsvpMachine';
import {
  cancelEvent,
  getEvent,
  getEventTemplate,
  getReasons,
  getSession,
  rescheduleRequest,
} from '../../services/schedularService';
import type {IParticipantType} from '../../types/IParticipantType';
import {getShortSha256Hash} from '../../utils/crypto';
import {isTimezoneSupported} from '../../utils/booking';

import type {
  IRootMachineContext,
  IRootMachineEvents,
  IRootMachineInput,
} from './rootMachine.types';
import {getRsvpStatus} from '../../utils/common';
import {isBrowserSupported} from '../../utils/browser';

import {rootMachineAnalyticsActions} from './rootMachine.analytics';


export const rootMachine = setup({
  types: {
    context: {} as IRootMachineContext,
    events: {} as IRootMachineEvents,
    input: {} as IRootMachineInput,
  },
  actors: {
    getEvent: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getEvent(input.eventId),
    ),
    getSession: fromPromise(async ({input}: {input: {sessionId: string}}) =>
      getSession(input.sessionId),
    ),
    getTemplateBySuffix: fromPromise(
      async ({input}: {input: {agentId: string; suffix: string}}) =>
        getEventTemplate(input.agentId, input.suffix),
    ),
    getReasons: fromPromise(async () => getReasons()),
    getOwner: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; attendee?: string};
      }): Promise<IParticipantType | undefined> => {
        const guestHash = await getShortSha256Hash(`guest_${input.eventId}`);
        if (guestHash === input.attendee) {
          return 'CANDIDATE';
        }
        const hostHash = await getShortSha256Hash(`host_${input.eventId}`);
        if (hostHash === input.attendee) {
          return 'HOST';
        }
        const interviewerHash = await getShortSha256Hash(
          `interviewer_${input.eventId}`,
        );
        if (interviewerHash === input.attendee) {
          return 'INTERVIEWER';
        }
        return undefined;
      },
    ),
    cancelEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          reason: string;
          viewerTimezone: string;
          attendee: string;
          aid: string;
        };
      }) =>
        cancelEvent(input.eventId, {
          viewer_timezone: input.viewerTimezone,
          attendee: input.attendee,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendRescheduleRequest: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; reason: string; aid: string};
      }) =>
        rescheduleRequest(input.eventId, {
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    generateAttendee: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getShortSha256Hash(`guest_${input.eventId}`),
    ),
    rsvpMachine,
    bookingMachine,
    identificationMachine,
  },
  guards: {
    isBrowserNotSupported: () => !isBrowserSupported(),
    isRescheduleLimitReached: ({context}) => {
      return (context.eventDetails as IEvent)?.reschedule_attempts_left === 0;
    },
    isEditBooking: ({context}) => {
      if (!context.currentRoute) {
        return false;
      }
      return ['cancel', 'reschedule', 'rsvp'].includes(context.currentRoute);
    },
    isSession: ({context}) => !!context.sessionId,
    isCancellationAndNotSequential: ({context}) => {
      const sequentialEvent =
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id);
      return context.currentRoute === 'cancel' && !sequentialEvent;
    },
    isRescheduleAndNotSequential: ({context}) => {
      const sequentialEvent =
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id);
      return context.currentRoute === 'reschedule' && !sequentialEvent;
    },
    isRsvp: ({context}) => context.currentRoute === 'rsvp',
    isCancellationNoticePass: ({context}) =>
      !!(context.eventDetails as IEvent)?.did_cancellation_notice_pass,
    isAttendeeAvailable: ({context}) => !!context.attendee,
    isHostOrInterviewer: ({context}) =>
      context.owner === 'HOST' || context.owner === 'INTERVIEWER',
    isCandidate: ({context}) => context.owner === 'CANDIDATE',
    isSessionExpired: ({context}) => !!context?.session?.is_expired,
    isTimezoneSupported: ({context}) =>
      context.timezone ? isTimezoneSupported(context.timezone) : false,
    isBookingSuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'BookingSuccess';
    },
    isSequentialEvent: ({context}) => {
      // Check if eventDetails is an array  for booking sequence OR event has group_id for any edit booking
      return (
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id)
      );
    },
    isRequestMoreAvailabilitySuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'RequestMoreAvailabilitySuccess';
    },
  },
  actions: {
    ...rootMachineAnalyticsActions,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCcD2qAuBiANgQwDsoBXPGAOmIAcI8MwBtABgF1FQrVYBLDb1AuxAAPRAEYAHADYAzOSYBOAExMALBLEB2KWMkKANCACeiALSbNE+duVKlszQFYmEgL6vDaTOVgY8ybGY2JBBOHj4BIVEEUzEZdXIZBRlHCxkpKQks1UMTGMcZOXUlVQySnSltd090DB8-AKwGMWCOLl5+QRDo0wlHJXJHZyYZCUVpKQUJXLMCoolyqXKxSqlqkC86339ApVbQ9oiu0GiV8lUlZQ0rqbixGfylKwvNC4VNJk-Stw8N2vI+ABbMAALwEYAAMqg8BBIE1WEIwh1It0zNpnk5rjIVBIFP0HqYUkx5GIFKoZDYxKpkqp1ptyMgwHhYAJYFCYdwiFgIODyJyAG6oADWYAZ-0ZzNZ7IgnKgCAFqAAxnROkEgojDp0oogmA8mHTxUyWQQ2dCZVyeQRRQqRWLvBLjaaOUR5QRBcqjmqWhrwlrUQhdcYdQb7UapWbZdzeTbRfSHeHnXKFR7VawGHsfcjjiIdXqQ3V4ybpZGwMg0MhyFR8BgAGaoZCAu0FsNFiMu5MqgRqhEhJFHbUBvO-OMt2AAUTL9awjIwyCM6t7mpRJ1mFnIfSeMhGEnSnykBKeVicowpkjEBSYmnzAO4wLBVoACtxFSLkFggaDebAwBgF21fcuOYxBSjjkFIpQFFInxKPEdj7kGMSqB8gyaFMdhZNim7XmA-JgAQGDFha0ZusKsb-DheEEW2SYkSmXZpj2-5ZgOgZ5Pqw7kbh+GEVAWCluWlbVnWDZNuQFHcdRrrup2BDdvsfZ+iug4IexNTeN+sA8AIPFRlafIkba9IaVpBA8VJSoyXJmb9v6zhiOQWgjIo4xMHojgPBSVh2GoYiXqoihPNexmdDp-H1oJdDCY2RlwCZZkdp6DHyUu2anEwlyJIU9hSI4ChiEoTgPCooHYkkqSaJcVJ4thXEYAAKmAgJVnQkLUbp1oGWR3jifVjXNfQ8W0ZZSXWYpQErGSgzpL56WqFSTiaA8O4DEwuUUpMFWpDlNWUQ1TXVq1iZ8ZOFb9VFok9Xt-WHeaNHSYlLB-gcAGpYgSjnlIiRUkojiqOSv2KPBeQ5ao5AKMo+V2Khc19NesL0A2nJgAA8gA7lab6Wh1gqGf88OloCSNoxj5l0bJI2Li9LFDmpdQ9TxE7ltOP5zk9CmAT05Xrv0uIFR856ONMCGmMS9jpOBhTpBVrwyGIQWxSFZpwmzKUDuoViVOlyjgb5BUGAhlRHpIOX5dof2FPLmmKzCyvepTzH+lS8SJK5P3SGMBSLQbO7yIo8Q6HiSFaJbJmM1OM6s4xz0O0pO6gRYihDCo6QQw830OT9q1knlCykteABG6BCrKABiOCoKj7X6TjoqF8Kpfl6jKtU474FWODW7Uinc1kmn+X2Uh4x6BkEivAXRcNxXVcxuQdfF0QZcV80yUt0pyeaOQ3mngH6XpH3KjnI4WjYtoKRi+P9cL430+dbPE9X0vGb2zZa+OEsDmbb5yiA0o+-EqoR9NAn1kP0WQF955QEXpXfARBSAUGoLQegzcY5AXeshTIsgAFJEyCkGQS0LgOTJHZck-klhXg4t1WqV0DphzfBHecUd2avQQMnOQq1Pi-WSMockv8EK-XsjiQomRfLpH6NeCuGNpTK0Yarf0vQdDrhKJ8JgqwdALAeO8UCusR5jHJDuGQ4j0alikRAJodsmIvyAvEYk4NwYjBypka4OQEJTH-qhPoeIVjLSUIYyRStTHphXig6IChLxgRWFBFRVJcR5Q0WMc4qEgFIXJBVPOFC6gSOMf4poMggmWNOBSOQ2J1DGySMeIGiBNEOXSr9fyssCqXHAbKAAysQRUipYrwjyWNU4mhSQfyeCk6kgchZ5EcqDN+6QkINMDgY9Jd9L5QFae0zpy9Roczelncgm0zbXGkM4sZfSBgANPluS8icFDXhgBjFqABBDA9ACCwjADfGuolrmljuQ8vCzzSbDUejI1e41tCfQpOwyofMVi8LGZ4lCthLypBULSeZyoCAdJwNWToUD9K8C6es5hsRqTxwKkfCQejcQALTlueyKi1ALBggoDIKRryovRZigQ2LOS4rWc-HpZh+6g39hYP6mRLznipa5eQUFigMqZY4FlhA2UyU5QQblT8LF8piGMAYRIFg1IquMCVNLpX0uwSAhVaKwAYuVY3BkLYS71kbFFZmkoCAOpErAYg+dCa-kBcE-lkgN79D1hYTEVJ3IIXPFkeQO5LBJFlh8BYFqlVHGxYWd1jZPXet4HwIiekZ70lZVa9lbrbXpsdT4L1Prc13Qsg9ZB+SA3xO-u8EYIr-IHPEHiUFHxFDdteJkchtNyBFutamst9qK1ZurSWE6EVawVsLYq4tNqK52tdRmyt2aHmyj+fWv1jaYioU+vlRQc0UhzSgvcSNAd5CXCPtiQW965nDtHSWtNk6RJhToZ+zNVbeANs1aYZIRRDyHnUG-VIachj2QKHiVChQ8rJDWPMxksBFQAAtIDEBwGAbFOBby8AAHKYAAEpMkw9I7pGyWE-W2aQ7+ZUfpITThcAYdghjYndnELI140OUYgDhvDtqCM+pIxgcjeABNmOo8woYn0nA6wKPYcGdgqXg03uoRlGRySyAKnxuAAmhP4cIxgcTknpOBPxQOMY9lwZZEuDrVySxWN2E3j9ZT3HRg-GHfxrDgncMfo3RW515aPX-t9bJgcphcrbPiCsXQ0hVoywjWM7VmU4ihuSA+lDvnDP+eMxO4L4Xt01teaRUSfnsOBaK8aTd06c27oSqmAFUX-SsWDKh-L1XhNrrC3+0rs6BJnUXYadDBWat9d-Vumd7Yhr7ra0pDrAYDPjZ60FurFbv0us2yVn1gGaPnvkHNUkeUn0qG0GnJJGcE5oNeDuOWXW1sBd66jBZECoHldtHPSeTcD2ap4woMGzgKr+VmTlaDLhBg-Uy3EPR8RVtGcm29n7D9K5Y2rhV1HkDG48o1TR0kssXaUnQiUaQ0GShgwuOSVyqFSTvURxN1773ftfdrvfHHj9FtAWW6pP4oZnuFbXdjz7MCSBkFFAglqB3mG+QSJxgquJFNjFGeIXKA8lhDAsG-Kk+VGfrdtVywI-2aMxfjooCaOdB0-TTpcOQMESikP8lkVI+uXsqu5eY6Oh67BA6AZxoYZLzySGheIS4QPssLRUD9LC8zscAGFl04AzVgBPSf6sRZlwOJIxJde5WPQVWW+s8hoKsLoGk4NLBAKHfzuoafLU4FaYNvN2MKv0nr+ipvs3a1kysry03uJiRTIKFVOYFIirR8GPBp4p71pNKIInhvXfGtcm-fO867eOeL87xFmte6WtZ7kbic4-lUjvDJc4eIeCEIqA01BLIZKKRODyvPqA2+rW0NT1v9PU7M8m4JQAvZDuDrFkLYLoKHiwmoHIGSksJkKUMlqMNeHQI8s8gAOJ4SfL0CmJVYvbkBQCoCH5KTRLEhAKkh-SbhHz2BpxxxgyhIfCXDaCVRIHfJPJgBgDoE3JYFYBvoyR4EEH-4DjzQbyuSyx9AfCCySCdq0aSqJyvAvCywAIoa-AECoCwjwAhCbDWZyITRD56qyyCwLCCypZmBLA2KGHxDSBPCfA15GQNAYBaFKSxC5SfSMrpSrTni7wsbCwXhgTCp2D5QJquTXjEAmjUCcABCQAABCaAqM34yADhQEsQV+H8fQgCmsUwXseQpg-kAioSQwoSyGlgag14H494N0kACRPQGg9kcuQCo8fQPkmRZgf0dmcB-hEwOcBmrqTot0lRZg1wCS9i7w3w6gUhpgMODk1Il4HwpQW4DiXRjotCfRiEMEmUUENgLg-spQB40ax4saZ4F4Nh-wpR4IT4L4pYyx+UUqt+rkZKeULgBIf0G8UE2IZQlQSQeUyKw69M1EyxvQlOLRrkW0emBU0G0BiKf0FwssWgQwIc1svR-ezCJSAy8BQw1ITwl2kaPsl6ug54WUSQXxteYkVCfUB0PElxkwNK2Imxp+kguUDwZQ64YOyWkS+UuWRJ+MiMVoxMFxiJ0WAxOg9gFwHiHwssDwuUuenwZIfQWgMEf0O0EkiYSxfJci-05wYw-uR8BRfSBIossgOmksEKA6GQcJ2k-iyxwyDk6xssOgWgmJeQgC2ymxDKJ4vkRx6kCsAgyp+OsuIwAwmQlUb8A6ZOrmGczguU1IIekhr+UCFJFUDkWp-kCcFULmLi1IiQoCBxCwf08q8yl0pJLU3p3umqaCbCzm0g8QeUGQ4ppQ2yMEaQEh5I4MviWSNsEAfxlgR4AC7s6QUsBQS0v0hCo896wJ8p8y34AAjsQJRNwHgDgGOLVGODKGZpgLchihXBUSqbHBoGDJYHZCIRwk0QgF-KDGMIHITnStiKaQQGOMIFQNwIyO2VuUBOoBvPYO4r9IUlDMYQgHuRlo7nMIkpcnHhzssh0ppJcUAnIP0G8HiJsbNGnFer7JtMydoLlFchgcgF8igWwcsSoJUA5JIAodYgsKrsechifhkOXheiaeOWAFOTOXOZEaBW0uBeoT6YIYmppllNoJeKeGRVoBpqkbgk8E5u6XUCoXwDWEYGBbFHhYLBvEIurhtAnD+R4e3FHn0khL2YSSOAxb4AALL1hgC3L8h4DcD4D5wWW8AyWsVyXPnRDvQEVJkaD2DvQMq27vCDAvFkjxDaCBrJorrjoVx-ErAEKjDJxRqGF9D7xaKyDKDFIUiMovpEk8HBVvZG6hURmJB6FRVuxqWXBWAaCrRvx+RjwopJ7vq2ppWdAPioAEaKh5AcXaHdneUpKFCnajCsbYhgRP6E72DLSBVjpYq1YcqOqhUwSgTBqJLa4aCUqRo8KHxqC5TSD2bqBDVVVTbFZOrjUOUBoKJZB0qQn9BTAVLHlanA6rSDITB2kbWrpvb9YzYr5QChURKJB6bCnaxQRqXgQTJaVuwuCBQVUN6bUPXTbfp-GVCgQJWuXvTBrbGRowZTTwYgZIbpB3XpV8iwj4TcA1jPj3V-G5SgSbiiEjCpAAJSH5R9BgTxVxBbifEUhu5C6ox-Fk5gRk6ZzUpVlnXjFubKJ9r2BQH-RM3I7kCibEZkYUb+Z4XvSJA5wx64gbj9mRopBA7UqjDpTfRk4i3M79as2fDs3FCX6uTc0EijyDDKIYIRLYjvA60bZjUiRRRZX+kc3G2kgRLUF0Za4gZpBTBTB22jVuq-7N4vV7XHlQTnDvkJaAWdzUFhLZxc19AvAB1bW7aNgQ1h2Mk8zpT6KnzKBXb+TnAOCzTJBBFPZI7M4i6NzLFLDHIqIqaQaawQHB7PD1l+1kp6w+ZEk4HM1Y0zl41kyxlh1y726c3aqyCrSsaKCKKZAFHqABnskjiC6i2ZVh2xBAKEVynl4h7aq24LCJCkEqLP7P5d1L0V0frL2QimYWbS1r0AJ3pwUFR8U04CUa3bJtwd10qTB8awD8hUBD3NVEHrTrjsJ9LBqXBpyJyHwwmMotEpWb6LLv7J67WAPjQcKDBwbCp9JV7F5vQqA0rwbkGkGlCv5IPL47pECQ1HxfS7IzLSlFR2lT6lWkjgR4hkpDWyUQVr2JYbzSm2n6EUrXol6nZMlbRnx2Kn3-Ad4f4nQTXEgKUBELBnb+5FRQFMmwEwEIGL3-Dkb6USbdYvacPsXFk0bwaby6xV7njkjX4wopBgSpB6AXCTCjDAXDrIE-JsEcGYGbmoOnCzE01wUqK-ViFpzvADBjDSolB2JxDuDuBAA */
  context: ({input}) => ({
    id: input.id,
    currentRoute: input.currentRoute,
    attendee: input.attendee,
    aid: input.aid,
    timezone: input.timezone,
    sessionId: input.sessionId,
    language: input.language,
    countyCode: input.countyCode,
  }),
  id: 'root',
  initial: 'start',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => event.language,
      }),
    },
  },
  states: {
    start: {
      always: [
        {
          target: 'unsupportedBrowser',
          guard: {
            type: 'isBrowserNotSupported',
          },
        },
        {
          target: 'timezoneLoaded',
          guard: {
            type: 'isTimezoneSupported',
          },
        },
        {
          target: 'timezonePicker',
        },
      ],
    },

    unsupportedBrowser: {
      type: 'final',
    },

    timezoneLoaded: {
      always: [
        {
          target: 'reasonsLoading',
        },
      ],
    },

    reasonsLoading: {
      invoke: {
        id: 'rootNew.reasonsLoading:invocation[0]',
        input: () => ({}),
        onDone: [
          {
            target: 'eventLoading',
            guard: {
              type: 'isEditBooking',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'sessionLoading',
            guard: {
              type: 'isSession',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'eventTemplateLoading',
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
        ],
        onError: {
          target: 'reasonsError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getReasons',
      },
    },

    reasonsError: {
      on: {
        retry: {
          target: 'reasonsLoading',
        },
      },
    },

    timezonePicker: {
      on: {
        'timezone.set': {
          target: 'timezoneLoaded',
          actions: assign({
            timezone: ({event}) => event.timezone,
          }),
        },
      },
    },

    eventLoading: {
      invoke: {
        id: 'rootNew.eventLoading:invocation[0]',
        input: ({context: {id}}) => ({eventId: id}),
        onDone: {
          target: 'determineOwner',
          actions: assign({
            eventDetails: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventLoadingError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getEvent',
      },
    },

    sessionLoading: {
      invoke: {
        input: ({context}) => {
          if (context.sessionId) {
            return {
              sessionId: context.sessionId,
            };
          }
          throw new Error('sessionId not found');
        },
        onDone: {
          target: 'sessionLoaded',
          actions: assign({
            session: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'sessionError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getSession',
      },
    },

    eventTemplateLoading: {
      invoke: {
        input: ({context: {currentRoute, id}}) => ({
          agentId: currentRoute,
          suffix: id,
        }),
        onDone: {
          target: 'bookingFlow',
          actions: assign({
            eventTemplate: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventTemplateError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getTemplateBySuffix',
      },
    },

    determineOwner: {
      invoke: {
        id: 'rootNew.determineOwner:invocation[0]',
        input: ({context}) => {
          if ((context.eventDetails as IEvent)?.id) {
            return {
              eventId: (context.eventDetails as IEvent)?.id,
              attendee: context?.attendee,
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'ownerLoaded',
          actions: assign({
            owner: ({event}) => event.output,
          }),
        },
        src: 'getOwner',
      },
    },

    eventLoadingError: {
      on: {
        retry: {
          target: 'eventLoading',
        },
      },
    },

    sessionLoaded: {
      always: [
        {
          target: 'sessionExpired',
          guard: {
            type: 'isSessionExpired',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
      entry: assign({
        language: ({context}) =>
          context.session?.guest_language || context.language,
      }),
    },

    sessionError: {
      on: {
        retry: {
          target: 'sessionLoading',
        },
      },
    },

    bookingFlow: {
      on: {
        'language.update': {
          actions: [
            assign({
              language: ({event}) => event.language,
            }),
            sendTo(
              ({system}) => system.get('bookingMachine')!,
              ({event}) => ({
                type: 'language.update',
                language: event.language,
              }),
            ),
          ],
        },
      },
      invoke: {
        id: 'bookingFlow',
        input: ({
          context: {
            timezone,
            eventTemplate,
            language,
            session,
            rescheduleSource,
          },
        }) => {
          if (timezone) {
            return {
              timezone,
              rescheduleSource,
              eventTemplate,
              language,
              session,
            };
          }
          throw new Error('timezone not found');
        },
        src: 'bookingMachine',
        systemId: 'bookingMachine',
        onDone: [
          {
            target: 'bookingSuccess',
            actions: assign({
              eventDetails: ({event}) => event.output.eventDetails,
            }),
            guard: {
              type: 'isBookingSuccess',
            },
          },
          {
            target: 'requestMoreAvailabilitySuccess',
            guard: {
              type: 'isRequestMoreAvailabilitySuccess',
            },
          },
          {
            target: 'notifySuccess',
          },
        ],
      },
    },

    eventTemplateError: {
      on: {
        retry: {
          target: 'eventTemplateLoading',
        },
      },
    },

    ownerLoaded: {
      always: [
        {
          target: 'cancellationFlow',
          guard: {
            type: 'isCancellationAndNotSequential',
          },
        },
        {
          target: 'rescheduleFlow',
          guard: {
            type: 'isRescheduleAndNotSequential',
          },
          actions: assign({rescheduleSource: 'confirmation_email'}),
        },
        {
          target: 'rsvpFlow',
          guard: {
            type: 'isRsvp',
          },
        },
        {
          guard: {
            type: 'isSequentialEvent',
          },
          target: 'sequentialEventEditNotAllowed',
        },
      ],
      entry: assign({
        language: ({context}) => {
          const eventDetails = context.eventDetails as IEvent;
          const users =
            context.owner === 'HOST' || context.owner === 'INTERVIEWER'
              ? eventDetails?.interviewers
              : eventDetails?.candidates;

          const currentUser = users?.find((user) => user.id === context.aid);
          return currentUser?.language || context.language;
        },
      }),
    },

    sequentialEventEditNotAllowed: {
      type: 'final',
    },

    sessionExpired: {
      type: 'final',
    },

    bookingSuccess: {
      always: [
        {
          target: 'sequentialBookingSuccess',
          guard: {
            type: 'isSequentialEvent',
          },
        },
        {
          target: 'generateAttendee',
        },
      ],
    },

    generateAttendee: {
      invoke: {
        input: ({context: {eventDetails}}) => ({
          eventId: (eventDetails as IEvent)?.id || '',
        }),
        src: 'generateAttendee',
        onDone: {
          target: 'attendeeGenerated',
          actions: assign({
            attendee: ({event}) => event.output,
            owner: 'CANDIDATE',
          }),
        },
      },
    },

    sequentialBookingSuccess: {
      type: 'final',
    },

    notifySuccess: {
      type: 'final',
    },

    requestMoreAvailabilitySuccess: {
      type: 'final',
    },

    cancellationFlow: {
      entry: [
        assign({cancellationSource: 'confirmation_email'}),
        'trackCancelMeetingInitiated',
      ],
      initial: 'init',
      states: {
        init: {
          always: [
            {
              target: 'cancellationPolicy',
              guard: {
                type: 'isCancellationNoticePass',
              },
            },
            {
              target: 'reasonForm',
              guard: {
                type: 'isAttendeeAvailable',
              },
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        cancellationPolicy: {
          type: 'final',
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
            submitting: {
              entry: assign({
                reason: ({event}) => {
                  assertEvent(event, 'reasonForm.submit');
                  return event.reason;
                },
              }),
              invoke: {
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id) {
                    return {
                      eventId: eventDetails?.id,
                      reason: event.reason,
                      viewerTimezone:
                        eventDetails?.primary_guest_timezone || '',
                      attendee: context.attendee || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.cancelSuccess',
                },
                onError: {
                  target: 'error',
                  actions: assign({
                    error: ({event}) => event.error,
                  }),
                },
                src: 'cancelEvent',
              },
            },
            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.cancellationFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'cancel',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
      },
    },

    rescheduleFlow: {
      entry: 'trackRescheduleMeetingInitiated',
      initial: 'init',
      states: {
        limitNotReached: {
          always: [
            {
              target: 'reasonForm',
              guard: {
                type: 'isHostOrInterviewer',
              },
            },
            {
              target: 'bookingFlow',
              guard: {
                type: 'isCandidate',
              },
              actions: assign({
                isReschedule: true,
              }),
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                  actions: assign({
                    reason: ({event}) => event.reason,
                  }),
                },
              },
            },

            submitting: {
              invoke: {
                id: 'rootNew.rescheduleFlow.rescheduleRequest.submitting:invocation[0]',
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id && context.aid) {
                    return {
                      eventId: eventDetails.id,
                      reason: event.reason || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId or aid not found');
                },
                onDone: {
                  target: '#root.RequestRescheduleSuccess',
                  reenter: true,
                },
                onError: {
                  target: 'error',
                  actions: assign({error: ({event}) => event.error}),
                },
                src: 'sendRescheduleRequest',
              },
            },

            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        bookingFlow: {
          on: {
            'language.update': {
              actions: [
                assign({
                  language: ({event}) => event.language,
                }),
                sendTo(
                  ({system}) => system.get('bookingFlow'),
                  ({event}) => ({
                    type: 'language.update',
                    language: event.language,
                  }),
                ),
              ],
            },
          },
          invoke: {
            id: 'bookingFlow',
            input: ({context}) => {
              const {timezone, attendee, language, rescheduleSource} = context;
              if (timezone) {
                return {
                  timezone,
                  rescheduleSource,
                  eventDetails: context.eventDetails as IEvent,
                  attendee,
                  language,
                };
              }
              throw new Error('timezone not found');
            },
            onDone: [
              {
                target: '#root.bookingSuccess',
                actions: assign({
                  eventDetails: ({event}) => event.output.eventDetails,
                  reason: ({event}) => event.output.rescheduleReason,
                }),
                guard: {
                  type: 'isBookingSuccess',
                },
              },
              {
                target: '#root.notifySuccess',
              },
            ],
            src: 'bookingMachine',
            systemId: 'bookingMachine',
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.rescheduleFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'reschedule',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
        init: {
          always: [
            {
              target: 'rescheduleLimitReached',
              guard: 'isRescheduleLimitReached',
            },
            {
              target: 'limitNotReached',
              reenter: true,
            },
          ],
        },

        rescheduleLimitReached: {},
      },
    },

    rsvpFlow: {
      invoke: {
        id: 'rootNew.rsvpFlow:invocation[0]',
        input: ({context}) => {
          const {aid} = context;
          const eventDetails = context.eventDetails as IEvent;
          const rsvpStatus = getRsvpStatus(eventDetails, aid, context.owner);
          if (aid && eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              aid,
              rsvpStatus,
            };
          }
          throw new Error('aid and eventId not found');
        },
        src: 'rsvpMachine',
        systemId: 'rsvpMachine',
      },
    },

    bookingCancelForm: {
      entry: 'trackCancelMeetingInitiated',
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
          actions: assign({
            reason: ({event}) => event.reason,
          }),
        },
      },
    },

    bookingCancelSubmitting: {
      invoke: {
        input: ({event, context}) => {
          const eventDetails = context.eventDetails as IEvent;
          assertEvent(event, 'bookingCancelForm.submit');
          if (eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              reason: event.reason,
              viewerTimezone: eventDetails?.primary_guest_timezone || '',
              attendee: context.attendee || '',
              aid: eventDetails?.candidates?.[0]?.id || context.aid || '',
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'cancelSuccess',
        },
        onError: {
          target: 'bookingCancelError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'cancelEvent',
      },
    },

    cancelSuccess: {
      type: 'final',
      entry: 'trackCancelMeetingConfirmed',
    },

    bookingCancelError: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
        },
      },
    },

    RequestRescheduleSuccess: {
      type: 'final',
    },

    attendeeGenerated: {
      on: {
        'reschedule.go': {
          target: 'rescheduleFlow',
          actions: assign({rescheduleSource: 'post_booking_page'}),
        },
        'cancellation.go': {
          target: 'bookingCancelForm',
          actions: assign({cancellationSource: 'post_booking_page'}),
        },
      },
    },
  },
});

export type IRootMachineSnapshot = SnapshotFrom<typeof rootMachine>;
